import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import {
  ArchiveIcon,
  ChatAlt2Icon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  FilterIcon,
  InboxIcon,
  RefreshIcon,
  ReplyIcon,
  PencilAltIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { RequestStatusBadge } from 'components/RequestStatusBadge';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { MERCHANT_REQUESTS } from 'gql/queries';
import {
  canManageRequests,
  MerchantRequestsQueryResult,
  MerchantRequest,
} from 'types/resolution';
import { isShipaidAdmin, SizeEnum } from 'utils';
import { withRequestReturnTo } from 'utils/requestNavigation';
import { getMerchantRequestsContext } from 'utils/merchantRequestsTransport';
import { requestHasRelevantSettlement } from 'utils/requestSettlementPresentation';
import { getRequestTypeLabel } from 'utils/requestTypePresentation';
import { OrderEditingRequestReview } from './OrderEditing/OrderEditingRequestReview';

const GROUPS = [
  { value: 'NEEDS_ACTION', label: 'Needs action', icon: InboxIcon },
  { value: 'WAITING', label: 'Waiting', icon: ClockIcon },
  { value: 'CLOSED', label: 'Closed', icon: CheckCircleIcon },
  { value: 'ALL', label: 'All', icon: ArchiveIcon },
];
const SORTS = [
  { value: 'UPDATED_AT_DESC', label: 'Recently updated' },
  { value: 'UPDATED_AT_ASC', label: 'Oldest update' },
  { value: 'CREATED_AT_DESC', label: 'Newest request' },
  { value: 'CREATED_AT_ASC', label: 'Oldest request' },
  { value: 'DUE_ASC', label: 'Due soonest' },
];
const STATUSES: Record<string, string[]> = {
  ISSUE: [
    'NEW',
    'IN_REVIEW',
    'PARTIALLY_RESOLVED',
    'RESOLVED',
    'DENIED',
    'ARCHIVED',
  ],
  RETURN: [
    'SUBMITTED',
    'APPROVED',
    'LABEL_READY',
    'AWAITING_DROPOFF',
    'IN_TRANSIT',
    'DELIVERED_PENDING_REVIEW',
    'RECEIVED',
    'RESOLVED',
    'DENIED',
    'EXPIRED',
  ],
  ORDER_EDITING: [
    'OPEN',
    'AWAITING_APPROVAL',
    'APPROVED',
    'PREPARING',
    'COMMITTING',
    'SUCCEEDED',
    'RESOLVED',
    'REJECTED',
    'EXPIRED',
    'FAILED',
  ],
};
const STANDARD_REASONS = new Set([
  'WRONG_ITEM',
  'DAMAGED',
  'NOT_AS_DESCRIBED',
  'MISSING_ITEM_OR_PARTS',
  'DOES_NOT_FIT',
  'CHANGED_MIND',
  'ARRIVED_LATE',
  'OTHER',
  'LOST',
  'STOLEN',
  'LATE',
]);
const label = (value: string) =>
  value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (first) => first.toUpperCase());
const dateLabel = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Unavailable'
    : date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};
const fieldClass =
  'mt-1 block min-w-0 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500';
const shiftedIsoDate = (value: string, offset = 0) => {
  const timestamp = Date.parse(value);
  const shifted = new Date(timestamp + offset);
  return Number.isFinite(shifted.valueOf()) ? shifted.toISOString() : undefined;
};
const filedDateBoundary = (value: string, exclusiveEnd = false) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = DateTime.fromISO(value, { zone: 'utc' });
  if (!date.isValid || date.year < 1) return undefined;
  const boundary = exclusiveEnd ? date.plus({ days: 1 }) : date;
  return boundary.year <= 9999 ? boundary.toISO() || undefined : undefined;
};
const validTimestamp = (value: string) => {
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
      value,
    )
  )
    return undefined;
  // Validate milliseconds while preserving the server's full timestamp precision.
  const validationValue = value.replace(/(\.\d{3})\d+/, '$1');
  const date = DateTime.fromISO(validationValue, { setZone: true });
  return date.isValid &&
    date.year >= 1 &&
    Number.isFinite(Date.parse(validationValue))
    ? value
    : undefined;
};
const isOrderedDateRange = (after: string, before: string) => {
  const milliseconds = Date.parse(after) - Date.parse(before);
  if (milliseconds !== 0) return milliseconds < 0;
  // Match the server's comparison without rounding away sub-millisecond ranges.
  const [afterFraction, beforeFraction] = [after, before].map(
    (value) => value.match(/\.(\d+)/)?.[1] ?? '',
  );
  const precision = Math.max(afterFraction.length, beforeFraction.length);
  return (
    afterFraction.padEnd(precision, '0') < beforeFraction.padEnd(precision, '0')
  );
};
const filterErrorMessages = new Set([
  'Creation dates must be valid ISO 8601 timestamps with a timezone.',
  'Created-after date must be before created-before date.',
  'Workflow filters are unavailable in this local preview because Return workflow details are incomplete.',
  'Return resolution filtering is unavailable in this local preview because requested resolution details are incomplete.',
]);

export function MerchantRequests() {
  const { storeId } = useStore();
  const { role, user } = useUser();
  const allowed = canManageRequests(role, isShipaidAdmin(user));
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const group = GROUPS.some((item) => item.value === params.get('group'))
    ? params.get('group')!
    : 'NEEDS_ACTION';
  const type = ['ISSUE', 'RETURN', 'ORDER_EDITING'].includes(
    params.get('type') || '',
  )
    ? params.get('type')!
    : 'ALL';
  const status = params.get('status') || undefined;
  const reason = params.get('reason') || undefined;
  const resolution = params.get('resolution') || undefined;
  const filedFrom = params.get('filedFrom') || '';
  const filedThrough = params.get('filedThrough') || '';
  const workflow = params.get('workflow') || undefined;
  const rawCreatedAfter = params.get('createdAfter') || '';
  const rawCreatedBefore = params.get('createdBefore') || '';
  const createdAfter = filedFrom
    ? filedDateBoundary(filedFrom)
    : validTimestamp(rawCreatedAfter);
  const createdBefore = filedThrough
    ? filedDateBoundary(filedThrough, true)
    : validTimestamp(rawCreatedBefore);
  const localDateError =
    (filedFrom && !createdAfter) || (filedThrough && !createdBefore)
      ? 'Choose valid filed dates, or clear the date filters to continue.'
      : (!filedFrom && rawCreatedAfter && !createdAfter) ||
        (!filedThrough && rawCreatedBefore && !createdBefore)
      ? 'Creation dates must be valid ISO 8601 timestamps with a timezone.'
      : createdAfter &&
        createdBefore &&
        !isOrderedDateRange(createdAfter, createdBefore)
      ? 'Created-after date must be before created-before date.'
      : undefined;
  const additionalFilterCount = [
    reason,
    resolution,
    filedFrom || rawCreatedAfter,
    filedThrough || rawCreatedBefore,
    workflow,
  ].filter(Boolean).length;
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(
    additionalFilterCount > 0,
  );
  const localPreview = Boolean(getMerchantRequestsContext());
  const orderId = params.get('orderId') || undefined;
  const search = (params.get('q') || '').trim();
  const orderBy = SORTS.some((item) => item.value === params.get('sort'))
    ? params.get('sort')!
    : 'UPDATED_AT_DESC';
  const requestedPage = Number(params.get('page') || 1);
  const page =
    Number.isSafeInteger(requestedPage) && requestedPage > 0
      ? requestedPage
      : 1;
  const [searchInput, setSearchInput] = useState(search);
  const [reviewNotice, setReviewNotice] = useState<{
    storeId?: string | null;
    requestId: string;
    text: string;
  }>();
  const previousStore = useRef(storeId);
  const limit = 20;

  useEffect(() => {
    setSearchInput(search);
  }, [search]);
  useEffect(() => {
    if (previousStore.current !== storeId) {
      previousStore.current = storeId;
      setSearchInput('');
      setParams({}, { replace: true });
    }
  }, [storeId, setParams]);

  const { data, loading, error, refetch } =
    useQuery<MerchantRequestsQueryResult>(MERCHANT_REQUESTS, {
      context: getMerchantRequestsContext(),
      variables: {
        input: {
          storeId,
          includeOrderEditing: true,
          orderId,
          type,
          group,
          status,
          reason,
          resolution,
          createdAfter,
          createdBefore,
          workflow,
          search: search || undefined,
          orderBy,
          limit,
          offset: (page - 1) * limit,
        },
      },
      skip: !storeId || !allowed || Boolean(localDateError),
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    });
  const payload = data?.merchantRequests;
  const current =
    !localDateError && payload?.storeId === storeId ? payload : undefined;
  const editRequest = params.get('editRequest');
  const selectedFromList = current?.nodes.find(
    (request) => request.type === 'ORDER_EDITING' && request.id === editRequest,
  );
  const selectedScope = `${storeId}:${editRequest}`;
  const retainedReview = useRef<{ scope: string; request: MerchantRequest }>();
  useLayoutEffect(() => {
    if (selectedFromList)
      retainedReview.current = {
        scope: selectedScope,
        request: selectedFromList,
      };
  }, [selectedFromList, selectedScope]);
  const selectedRequest =
    selectedFromList ||
    ((loading || error) && retainedReview.current?.scope === selectedScope
      ? retainedReview.current.request
      : undefined);
  const filterError = error?.graphQLErrors?.find(({ message }) =>
    filterErrorMessages.has(message),
  )?.message;
  const returnTo = `${location.pathname}${location.search}`;
  const update = (
    values: Record<string, string | undefined>,
    resetPage = true,
  ) => {
    const next = new URLSearchParams(params);
    if (resetPage) next.delete('page');
    next.delete('editRequest');
    Object.entries(values).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key),
    );
    setParams(next);
  };
  const statuses =
    type === 'ALL'
      ? Array.from(new Set(Object.values(STATUSES).flat()))
      : STATUSES[type];

  if (!storeId) return <p className="p-6">Choose a store to view requests.</p>;
  if (!allowed)
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Requests</h1>
        <p className="mt-3">
          An Admin or Manager role is required to view this workspace.
        </p>
        <Link
          className="mt-3 inline-block text-blue-700 hover:underline"
          to="/claims"
        >
          View Issues
        </Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 pb-8 sm:px-6 md:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-blue-50 p-3 text-blue-700">
            <InboxIcon aria-hidden="true" className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-gray-900">
              Request queue
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Review requests and see who acts next.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={loading || Boolean(localDateError)}
          onClick={() => {
            if (!localDateError) void refetch();
          }}
        >
          <RefreshIcon
            aria-hidden="true"
            className={classNames(
              'h-4 w-4',
              loading && 'motion-safe:animate-spin',
            )}
          />
          Refresh
        </button>
      </div>
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
        role="group"
        aria-label="Request queues"
      >
        {GROUPS.map((item) => {
          const selected = group === item.value;
          const count =
            !loading && !error && current
              ? current.counts.find((entry) => entry.group === item.value)
                  ?.count ?? 0
              : null;
          const Icon = item.icon;
          return (
            <button
              key={item.value}
              type="button"
              aria-pressed={selected}
              aria-label={`${item.label}${count !== null ? ` (${count})` : ''}`}
              onClick={() => update({ group: item.value })}
              className={classNames(
                'min-w-0 rounded-xl border p-4 text-left shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                selected
                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-gray-50',
              )}
            >
              <span className="flex items-center justify-between gap-2 text-xs font-medium">
                {item.label}
                <Icon aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
              </span>
              <span
                className={classNames(
                  'mt-2 block text-2xl font-semibold tracking-tight tabular-nums',
                  selected ? 'text-blue-700' : 'text-gray-900',
                )}
              >
                {count === null ? '—' : count.toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>
      <section
        aria-label="Request filters"
        className="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-sm sm:p-5"
      >
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-3 xl:grid-cols-5">
          <form
            role="search"
            className="min-w-0 sm:col-span-3 xl:col-span-2"
            onSubmit={(event) => {
              event.preventDefault();
              update({ q: searchInput.trim() || undefined });
            }}
          >
            <label
              htmlFor="request-search"
              className="min-w-0 text-xs font-medium text-gray-600"
            >
              Search requests
            </label>
            <div className="mt-1 flex gap-2">
              <div className="relative min-w-0 flex-1">
                <SearchIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400"
                />
                <input
                  id="request-search"
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Order, request or customer"
                  className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Search
              </button>
            </div>
          </form>
          <label className="min-w-0 text-xs font-medium text-gray-600">
            Request type
            <select
              value={
                type === 'RETURN' && resolution === 'EXCHANGE'
                  ? 'EXCHANGE'
                  : type
              }
              className={fieldClass}
              onChange={(event) =>
                update({
                  type:
                    event.target.value === 'EXCHANGE'
                      ? 'RETURN'
                      : event.target.value,
                  resolution:
                    event.target.value === 'ORDER_EDITING'
                      ? undefined
                      : event.target.value === 'EXCHANGE'
                      ? 'EXCHANGE'
                      : type === 'RETURN' && resolution === 'EXCHANGE'
                      ? undefined
                      : resolution,
                  status: undefined,
                  workflow: ['ISSUE', 'ORDER_EDITING'].includes(
                    event.target.value,
                  )
                    ? undefined
                    : workflow,
                  reason:
                    event.target.value === 'ORDER_EDITING' ? undefined : reason,
                })
              }
            >
              <option value="ALL">All requests</option>
              <option value="ISSUE">Issues</option>
              <option value="RETURN">Returns &amp; exchanges</option>
              <option value="EXCHANGE">Exchanges</option>
              <option value="ORDER_EDITING">Order Editing</option>
            </select>
          </label>
          <label className="min-w-0 text-xs font-medium text-gray-600">
            Workflow status
            <select
              value={status || ''}
              className={fieldClass}
              onChange={(event) =>
                update({ status: event.target.value || undefined })
              }
            >
              <option value="">All statuses</option>
              {status && !statuses.includes(status) && (
                <option value={status}>{label(status)}</option>
              )}
              {statuses.map((value) => (
                <option key={value} value={value}>
                  {label(value)}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-0 text-xs font-medium text-gray-600">
            Sort requests
            <select
              value={orderBy}
              className={fieldClass}
              onChange={(event) => update({ sort: event.target.value })}
            >
              {SORTS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-4">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-expanded={additionalFiltersOpen}
            aria-controls="additional-request-filters"
            onClick={() => setAdditionalFiltersOpen(!additionalFiltersOpen)}
          >
            <FilterIcon aria-hidden="true" className="h-4 w-4" />
            More filters
            {additionalFilterCount ? ` (${additionalFilterCount})` : ''}
            <ChevronDownIcon
              aria-hidden="true"
              className={classNames(
                'h-4 w-4 transition-transform',
                additionalFiltersOpen && 'rotate-180',
              )}
            />
          </button>
          {additionalFiltersOpen && (
            <div
              id="additional-request-filters"
              className="mt-4 border-t border-gray-200 pt-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                <label className="text-sm font-medium text-gray-700">
                  Reason
                  <input
                    className={fieldClass}
                    value={reason || ''}
                    placeholder="Exact reason"
                    onChange={(event) =>
                      update({ reason: event.target.value || undefined })
                    }
                  />
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Resolution
                  <select
                    className={fieldClass}
                    value={resolution || ''}
                    onChange={(event) =>
                      update({ resolution: event.target.value || undefined })
                    }
                  >
                    <option value="">All resolutions</option>
                    {[
                      'IN_REVIEW',
                      'REFUND',
                      'REORDER',
                      'EXCHANGE',
                      'STORE_CREDIT',
                      'DISCOUNT_CODE',
                      'DENY',
                      'OTHER',
                    ].map((value) => (
                      <option value={value} key={value}>
                        {label(value)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Filed from (UTC)
                  <input
                    type="date"
                    className={fieldClass}
                    value={filedFrom || createdAfter?.slice(0, 10) || ''}
                    max={filedThrough || undefined}
                    onChange={(event) =>
                      update({
                        filedFrom: event.target.value || undefined,
                        createdAfter: undefined,
                      })
                    }
                  />
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Filed through (UTC)
                  <input
                    type="date"
                    className={fieldClass}
                    value={
                      filedThrough ||
                      (createdBefore
                        ? shiftedIsoDate(createdBefore, -86400000)?.slice(
                            0,
                            10,
                          ) || ''
                        : '')
                    }
                    min={filedFrom || undefined}
                    onChange={(event) =>
                      update({
                        filedThrough: event.target.value || undefined,
                        createdBefore: undefined,
                      })
                    }
                  />
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Return / exchange workflow
                  <select
                    className={fieldClass}
                    value={workflow || ''}
                    disabled={localPreview}
                    onChange={(event) =>
                      update({
                        workflow: event.target.value || undefined,
                        type: event.target.value ? 'RETURN' : type,
                      })
                    }
                  >
                    <option value="">All workflows</option>
                    <option value="INSPECTION">Needs inspection</option>
                    <option value="PENDING_REFUND">Pending refund</option>
                    <option value="FLAGGED">Flagged</option>
                  </select>
                </label>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
                <p>
                  {localPreview
                    ? 'Workflow views require the full backend and are unavailable in this local preview.'
                    : 'Resolution filters use the issue resolution or the customer’s requested return or exchange resolution.'}
                </p>
                <button
                  type="button"
                  className="font-medium text-blue-700 hover:underline"
                  onClick={() =>
                    update({
                      reason: undefined,
                      resolution: undefined,
                      filedFrom: undefined,
                      filedThrough: undefined,
                      createdAfter: undefined,
                      createdBefore: undefined,
                      workflow: undefined,
                    })
                  }
                >
                  Clear additional filters
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      {orderId && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <span>Showing requests for one order</span>
          <Link
            className="text-blue-700 hover:underline"
            to={withRequestReturnTo(`/orders/${orderId}`, returnTo)}
          >
            View order
          </Link>
          <button
            type="button"
            className="text-blue-700 hover:underline"
            onClick={() => update({ orderId: undefined })}
          >
            Show all orders
          </button>
        </div>
      )}
      {reviewNotice?.storeId === storeId && (
        <p
          role="status"
          className="rounded-lg bg-green-50 p-3 text-sm text-green-800"
        >
          {reviewNotice.text}
        </p>
      )}
      {editRequest &&
        (selectedRequest ? (
          <OrderEditingRequestReview
            key={`${storeId}:${selectedRequest.id}`}
            request={selectedRequest}
            disabled={loading || Boolean(error) || Boolean(localDateError)}
            onClose={() => update({ editRequest: undefined }, false)}
            onReviewed={() => {
              setReviewNotice({
                storeId,
                requestId: selectedRequest.id,
                text: 'Request review submitted.',
              });
              void Promise.resolve(refetch()).catch(() => undefined);
            }}
          />
        ) : (
          current &&
          !loading &&
          !error && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p>
                {reviewNotice?.storeId === storeId &&
                reviewNotice.requestId === editRequest
                  ? 'The reviewed request moved out of this queue.'
                  : 'This Order Editing request is not in the current results. Refresh the list or adjust your filters.'}
              </p>
              <button
                type="button"
                className="mt-2 font-medium underline"
                onClick={() => update({ editRequest: undefined }, false)}
              >
                Close review
              </button>
            </div>
          )
        ))}
      {error || localDateError ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800"
        >
          <p>
            {localDateError ||
              filterError ||
              'We could not load requests. Try again or narrow your filters.'}
          </p>
          {!localDateError && (
            <Button
              className="mt-3"
              size={SizeEnum.sm}
              onClick={() => {
                void refetch();
              }}
            >
              Try again
            </Button>
          )}
        </div>
      ) : loading || !current ? (
        <div
          role="status"
          className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <p className="text-sm text-gray-500">Loading requests…</p>
          <div
            aria-hidden="true"
            className="mt-5 space-y-4 motion-safe:animate-pulse"
          >
            {[0, 1, 2].map((row) => (
              <div key={row} className="h-10 rounded-lg bg-gray-100" />
            ))}
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-5">
            <h3 className="text-sm font-semibold text-gray-900">
              {GROUPS.find((item) => item.value === group)?.label}
            </h3>
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium tabular-nums text-gray-600">
              {current.totalCount.toLocaleString()}{' '}
              {current.totalCount === 1 ? 'result' : 'results'}
            </span>
          </div>
          <div
            role="region"
            aria-label="Request results"
            tabIndex={0}
            className="overflow-x-auto focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
          >
            <table
              className="w-full min-w-[1100px] divide-y divide-gray-200 text-left text-sm"
              aria-label="Requests"
            >
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  {[
                    'Request',
                    'Order / customer',
                    'Items / reason',
                    'Status / outcome',
                    'Next step',
                    'Due / updated',
                  ].map((column, index) => (
                    <th
                      key={column}
                      scope="col"
                      className={classNames(
                        'px-4 py-3.5',
                        index === 0 && 'sticky left-0 z-10 bg-gray-50',
                      )}
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {current.nodes.map((request) => (
                  <tr
                    key={`${request.type}:${request.id}`}
                    className="group align-top transition-colors hover:bg-blue-50"
                  >
                    <td className="sticky left-0 z-10 bg-white px-4 py-4 group-hover:bg-blue-50">
                      <Link
                        className="whitespace-nowrap rounded font-semibold text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        to={
                          request.type === 'ORDER_EDITING'
                            ? (() => {
                                const next = new URLSearchParams(params);
                                next.set('editRequest', request.id);
                                return `${
                                  location.pathname
                                }?${next.toString()}`;
                              })()
                            : withRequestReturnTo(
                                `/${
                                  request.type === 'ISSUE'
                                    ? 'claims'
                                    : 'returns'
                                }/${request.id}`,
                                returnTo,
                              )
                        }
                      >
                        {request.number}
                      </Link>
                      <p
                        className={classNames(
                          'mt-2 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium',
                          request.type === 'ISSUE'
                            ? 'bg-blue-50 text-blue-700'
                            : request.type === 'ORDER_EDITING'
                            ? 'bg-amber-50 text-amber-800'
                            : 'bg-violet-50 text-violet-700',
                        )}
                      >
                        {request.type === 'ISSUE' ? (
                          <ChatAlt2Icon
                            aria-hidden="true"
                            className="h-3 w-3"
                          />
                        ) : request.type === 'ORDER_EDITING' ? (
                          <PencilAltIcon
                            aria-hidden="true"
                            className="h-3 w-3"
                          />
                        ) : (
                          <ReplyIcon aria-hidden="true" className="h-3 w-3" />
                        )}
                        {request.type === 'ORDER_EDITING'
                          ? 'Order Editing'
                          : getRequestTypeLabel(request)}
                      </p>
                    </td>
                    <td className="min-w-[180px] max-w-[220px] px-4 py-4">
                      {request.orderId ? (
                        <Link
                          className="rounded font-medium text-gray-900 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          to={withRequestReturnTo(
                            `/orders/${request.orderId}`,
                            returnTo,
                          )}
                        >
                          {request.orderNumber || 'View order'}
                        </Link>
                      ) : (
                        <p className="text-gray-500">
                          Order details unavailable
                        </p>
                      )}
                      <p className="mt-1 break-words text-gray-700">
                        {request.customerName || 'Customer unavailable'}
                      </p>
                      <p className="mt-1 break-words text-xs text-gray-500">
                        {request.customerEmail}
                      </p>
                    </td>
                    <td className="min-w-[200px] max-w-xs px-4 py-4">
                      {request.type === 'ORDER_EDITING' && (
                        <p className="text-sm text-gray-700">
                          {request.summary || 'Order change requested'}
                        </p>
                      )}
                      {request.items?.slice(0, 2).map((item, index) => (
                        <div
                          key={`${item.orderItemId || index}:${index}`}
                          className={index ? 'mt-2' : ''}
                        >
                          <p className="font-medium text-gray-800">
                            {item.quantity == null ? '' : `${item.quantity} × `}
                            {item.title || 'Item unavailable'}
                          </p>
                          {item.reason && (
                            <p className="mt-1 text-xs text-gray-500">
                              {STANDARD_REASONS.has(item.reason)
                                ? label(item.reason)
                                : item.reason}
                            </p>
                          )}
                        </div>
                      ))}
                      {request.items && request.items.length > 2 && (
                        <p className="mt-1 text-xs text-gray-600">
                          +{request.items.length - 2} more items
                        </p>
                      )}
                      {!request.items?.length &&
                        request.type !== 'ORDER_EDITING' &&
                        'Items unavailable'}
                    </td>
                    <td className="min-w-[160px] max-w-[210px] px-4 py-4">
                      <RequestStatusBadge
                        status={request.status}
                        label={request.statusLabel}
                      />
                      {request.outcome && (
                        <p className="mt-2 text-xs text-gray-700">
                          {request.outcome}
                        </p>
                      )}
                      {request.settlementStatus &&
                        requestHasRelevantSettlement(request) && (
                          <p className="mt-1 text-xs text-gray-600">
                            {request.settlementType
                              ? label(request.settlementType)
                              : 'Settlement'}
                            : {label(request.settlementStatus)}
                          </p>
                        )}
                    </td>
                    <td className="min-w-[170px] max-w-[210px] px-4 py-4">
                      <p className="font-medium leading-5 text-gray-800">
                        {request.nextActionLabel}
                      </p>
                      {request.responsibleParty &&
                        request.responsibleParty !== 'NONE' && (
                          <p className="mt-2 inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            {label(request.responsibleParty)}
                          </p>
                        )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-xs tabular-nums">
                      {request.dueAt && (
                        <p className="mb-2 font-medium text-gray-800">
                          Due {dateLabel(request.dueAt)}
                        </p>
                      )}
                      <p className="text-gray-500">Updated</p>
                      <p className="mt-1 text-gray-700">
                        {dateLabel(request.updatedAt)}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {current.nodes.length === 0 && (
              <div className="px-5 py-12 text-center">
                <InboxIcon
                  aria-hidden="true"
                  className="mx-auto h-8 w-8 text-gray-400"
                />
                <p className="mt-3 text-sm font-medium text-gray-900">
                  No requests match this view.
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Choose another queue or adjust your filters.
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm sm:px-5">
            <p className="text-xs tabular-nums text-gray-600">
              {current.totalCount} requests · Page {page} of{' '}
              {Math.max(1, Math.ceil(current.totalCount / limit))}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                aria-label="Previous page"
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => update({ page: String(page - 1) }, false)}
              >
                <ChevronLeftIcon aria-hidden="true" className="h-4 w-4" />{' '}
                Previous
              </button>
              <button
                type="button"
                aria-label="Next page"
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!current.hasMore}
                onClick={() => update({ page: String(page + 1) }, false)}
              >
                Next <ChevronRightIcon aria-hidden="true" className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
